import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    getCustomerServiceCredits,
    updateCustomerCredit,
} from '@/modules/Customer/components/Detail/Credits/services'
import {
    CustomerCredit,
    UpdateCustomerCredit,
} from '@/modules/Customer/components/Detail/Credits/types'
import { ModalProps } from '@/types/shared'
import { formatDateWithFormatter } from '@/utils/function'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'
import { schemaUpdateCreditTransactionModal } from './schema'

export interface UpdateCreditModalProps extends ModalProps {
    customerID: number
    creditID: number
    credit: CustomerCredit
}

const CreateCreditModal = ({
    onClose,
    open,
    customerID,
    creditID,
    credit,
}: UpdateCreditModalProps) => {
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS_CREDITS_SERVICES, customerID],
        queryFn: () => getCustomerServiceCredits({ customerID: customerID }),
    })

    const formControl = useForm<UpdateCustomerCredit>({
        defaultValues: {
            credit_name: credit.credit_name,
            credit_warningable: credit.credit_warningable,
            credit_warning_amount: credit.credit_warning_amount,
            credit_effective_date: credit.credit_effective_date,
            credit_expiry_date: credit.credit_expiry_date,
            credit_lockable: credit.credit_lockable,
            service_ids: credit.service_ids,
        },
        resolver: yupResolver(schemaUpdateCreditTransactionModal as any),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: updateCustomerCredit },
        {
            reactQueryKeys: QUERY_KEYS.CUSTOMERS,
            successMessage: t('add-new-subcontract-success'),
            onSuccess: () => {
                onClose()
            },
        }
    )

    const handleCreate = (values: UpdateCustomerCredit) => {
        mutateAsync({
            customerID,
            creditID,
            data: {
                ...values,
                credit_effective_date: formatDateWithFormatter(
                    values.credit_effective_date,
                    'YYYY-MM-DD'
                ),
                credit_expiry_date:
                    values.credit_expiry_date &&
                    formatDateWithFormatter(
                        values.credit_expiry_date,
                        'YYYY-MM-DD'
                    ),
            },
        })
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <Dialog
            title={t('customers.credits.update')}
            onClose={onClose}
            open={open}
        >
            <GridHookForm
                formControl={formControl}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outlined',
                        handler: onClose,
                        disabled: isPending,
                    },
                    {
                        title: t('credit.transaction.create'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isPending,
                        disabled: isPending,
                    },
                ]}
                onSubmit={handleCreate}
                data={[
                    {
                        formItemProps: {
                            name: 'credit_name',
                            type: 'textfield',
                            label: t('credit_name'),
                            placeholder: t('credit_name'),
                        },
                        className: 'col-span-4',
                    },
                    {
                        formItemProps: {
                            name: 'credit_warningable',
                            type: 'checkbox',
                            label: t('credit_warningable'),
                            placeholder: t('credit_warningable'),
                        },
                        className: !formControl.watch(`credit_warningable`)
                            ? 'col-span-4'
                            : 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'credit_warning_amount',
                            type: 'number',
                            label: t('credit_warning_amount'),
                            placeholder: t('credit_warning_amount'),
                        },
                        className: !formControl.watch(`credit_warningable`)
                            ? 'hidden'
                            : 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'credit_effective_date',
                            type: 'datepicker',
                            label: t('credit_effective_date'),
                            placeholder: t('credit_effective_date'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'credit_expiry_date',
                            type: 'datepicker',
                            label: t('credit_expiry_date'),
                            placeholder: t('credit_expiry_date'),
                        },
                        className: 'col-span-2',
                    },
                    {
                        formItemProps: {
                            name: 'service_ids',
                            type: 'select',
                            multiple: true,
                            label: t('service_ids'),
                            placeholder: t('service_ids'),
                            options: data?.results?.map((service) => {
                                return {
                                    value: service.service_id,
                                    label: `${service?.service_id} - ${service.service_name} (${t(service?.service_status)})`,
                                }
                            }),
                        },
                        className: 'col-span-4',
                    },
                ]}
            />
        </Dialog>
    )
}

export default CreateCreditModal
